import React from "react";
import { useToast } from "../components/ResultsPrimitives.jsx";
import {
  closeExportDocumentWindow,
  downloadResultsExport,
  getResultsExportHistory,
  getResultsExportStatus,
  isExportApiError,
  launchResultsExport,
  markExportDocumentWindowFailed,
  openExportDocumentWindow,
} from "../lib/exportApi.js";
import {
  BROWSER_DOWNLOAD_DESTINATION_LABEL,
  isSaveLocationCancelledError,
  prepareBrowserSaveTarget,
  SAVE_MODE_BROWSER_DOWNLOAD,
  SAVE_MODE_FILE_SYSTEM,
  XLSX_MEDIA_TYPE,
} from "../utils/browserSaveTarget.js";
import { appendDispatchDateToUrl } from "../utils/dispatchDateUrls.js";
import { clearAppStatusNoticeBySource, dispatchAppStatusNotice } from "../utils/appStatusNotice.js";

const DEFAULT_RESULTS_EXPORT_STATE = {
  downloadUrl: "",
  errorCode: "",
  exportId: "",
  expiresAt: null,
  fingerprint: "",
  finishedAt: null,
  message: "",
  pollIntervalMs: 2000,
  resultUrl: "",
  rowCount: 0,
  startedAt: null,
  status: "idle",
};

const RESULTS_EXPORT_LOADING_TOAST = {
  type: "loading",
  title: "Export des résultats...",
  message: "Préparation du fichier XLSX en arrière-plan. Vous pouvez continuer à naviguer.",
};

const RESULTS_EXPORT_BROWSER_DESTINATION_LABEL = BROWSER_DOWNLOAD_DESTINATION_LABEL;
const RESULTS_EXPORT_DEFAULT_FILE_NAME = "grain_results_export.xlsx";
const RESULTS_EXPORT_READY_ROUTE = "/results?downloadExport=1";
const ResultsExportTrackerContext = React.createContext(null);

function buildDefaultResultsExportState() {
  return { ...DEFAULT_RESULTS_EXPORT_STATE };
}

function normalizeFingerprint(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveResultsReadyRoute(resultUrl = "") {
  const normalizedResultUrl = typeof resultUrl === "string" ? resultUrl.trim() : "";
  return appendDispatchDateToUrl(normalizedResultUrl || RESULTS_EXPORT_READY_ROUTE);
}

function resolveFiniteNumber(value, fallbackValue) {
  if (value == null || value === "") {
    return fallbackValue;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallbackValue;
}

function resolveOptionalTimestamp(value) {
  return typeof value === "string" && value.trim() ? value : null;
}

function buildPendingResultsExportState({
  exportId,
  fingerprint = "",
  payload = null,
  pollIntervalFallback = 2000,
  resultUrl = "",
  rowCountFallback = 0,
  startedAtFallback = null,
} = {}) {
  return {
    ...buildDefaultResultsExportState(),
    exportId,
    fingerprint: normalizeFingerprint(fingerprint || payload?.fingerprint),
    message: typeof payload?.message === "string" ? payload.message : "Export en préparation.",
    pollIntervalMs: resolveFiniteNumber(
      payload?.tracking?.recommended_poll_interval_ms,
      resolveFiniteNumber(payload?.recommended_poll_interval_ms, pollIntervalFallback),
    ),
    resultUrl: resolveResultsReadyRoute(resultUrl),
    rowCount: resolveFiniteNumber(payload?.row_count, rowCountFallback),
    startedAt: resolveOptionalTimestamp(payload?.started_at) || startedAtFallback,
    status: "pending_async",
  };
}

function buildReadyResultsExportState({
  exportId,
  fingerprint = "",
  payload = null,
  resultUrl = "",
  rowCountFallback = 0,
  startedAtFallback = null,
} = {}) {
  return {
    ...buildDefaultResultsExportState(),
    downloadUrl: typeof payload?.download_url === "string" ? payload.download_url : "",
    exportId,
    expiresAt: resolveOptionalTimestamp(payload?.expires_at),
    fingerprint: normalizeFingerprint(fingerprint || payload?.fingerprint),
    finishedAt: resolveOptionalTimestamp(payload?.finished_at),
    message: typeof payload?.message === "string" ? payload.message : "Export prêt au téléchargement.",
    resultUrl: resolveResultsReadyRoute(resultUrl),
    rowCount: resolveFiniteNumber(payload?.row_count, rowCountFallback),
    startedAt: resolveOptionalTimestamp(payload?.started_at) || startedAtFallback,
    status: "ready_async",
  };
}

function formatExportClockTime(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  const resolvedDate = new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("fr-CA", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(resolvedDate);
}

function buildCompletedResultsExportNotice(exportState) {
  const finishedAtLabel = formatExportClockTime(exportState.finishedAt);
  return {
    source: "results_export",
    status: "completed",
    eyebrow: "Export Résultats",
    title: "Export Résultats prêt",
    message: finishedAtLabel
      ? `Le fichier XLSX est prêt depuis ${finishedAtLabel}.`
      : "Le fichier XLSX est prêt au téléchargement.",
    detailItems: [
      {
        key: "row-count",
        label: "Lignes exportées",
        value: String(exportState.rowCount || 0),
      },
      ...(exportState.expiresAt
        ? [{
            key: "expires-at",
            label: "Disponible jusqu'à",
            value: exportState.expiresAt,
          }]
        : []),
    ],
    finishedAt: exportState.finishedAt,
    resultActionLabel: "Télécharger l'export",
    resultUrl: resolveResultsReadyRoute(exportState?.resultUrl),
    statusLabel: "Téléchargement prêt",
  };
}

function buildRunningResultsExportNotice({
  destinationLabel = RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
  message = "Préparation du fichier XLSX. Le téléchargement sera ensuite géré par votre navigateur.",
  rowCount = 0,
  startedAt = null,
} = {}) {
  return {
    source: "results_export",
    status: "running",
    eyebrow: "Export Résultats",
    title: "Export Résultats en cours",
    message,
    detailItems: [
      ...(rowCount > 0
        ? [{
            key: "row-count",
            label: "Lignes à exporter",
            value: String(rowCount),
          }]
        : []),
      {
        key: "download-destination",
        label: "Emplacement",
        value: destinationLabel,
      },
    ],
    startedAt,
    statusLabel: "Préparation",
  };
}

function buildDownloadedResultsExportNotice({
  destinationLabel = RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
  fileName = RESULTS_EXPORT_DEFAULT_FILE_NAME,
  finishedAt = null,
  rowCount = 0,
  saveMode = SAVE_MODE_BROWSER_DOWNLOAD,
} = {}) {
  const savedToChosenLocation = saveMode === SAVE_MODE_FILE_SYSTEM;

  return {
    source: "results_export",
    status: "completed",
    eyebrow: "Export Résultats",
    title: savedToChosenLocation ? "Export Résultats enregistré" : "Export Résultats téléchargé",
    message: savedToChosenLocation
      ? `Le fichier ${fileName} a été enregistré à l’emplacement choisi.`
      : `Le téléchargement de ${fileName} a été lancé. Vérifiez l'icône de téléchargements du navigateur ou le dossier Téléchargements.`,
    detailItems: [
      {
        key: "file-name",
        label: "Fichier",
        value: fileName,
      },
      ...(rowCount > 0
        ? [{
            key: "row-count",
            label: "Lignes exportées",
            value: String(rowCount),
          }]
        : []),
      {
        key: "download-destination",
        label: "Emplacement",
        value: destinationLabel,
      },
    ],
    finishedAt,
    statusLabel: savedToChosenLocation ? "Fichier enregistré" : "Téléchargement lancé",
  };
}

function buildFailedResultsExportNotice({
  detailItems = [],
  message,
  title,
}) {
  return {
    source: "results_export",
    status: "failed",
    eyebrow: "Export Résultats",
    title,
    message,
    detailItems,
    statusLabel: "Action requise",
  };
}

export function ResultsExportProvider({ children }) {
  const { dismissToast, showToast, updateToast } = useToast();
  const [exportState, setExportState] = React.useState(buildDefaultResultsExportState);
  const autoOpenFingerprintRef = React.useRef("");
  const documentWindowRef = React.useRef(null);
  const loadingToastIdRef = React.useRef(null);
  const exportStateRef = React.useRef(exportState);
  const historyAbortControllerRef = React.useRef(null);
  const hydratedFingerprintRef = React.useRef("");
  const pendingSaveTargetRef = React.useRef(null);
  const pollAbortControllerRef = React.useRef(null);

  React.useEffect(() => {
    exportStateRef.current = exportState;
  }, [exportState]);

  React.useEffect(() => () => {
    pollAbortControllerRef.current?.abort();
    historyAbortControllerRef.current?.abort();
  }, []);

  const clearResultsExportNotice = React.useCallback(() => {
    clearAppStatusNoticeBySource("results_export");
  }, []);

  const clearPendingSaveTarget = React.useCallback(() => {
    pendingSaveTargetRef.current = null;
  }, []);

  const publishRunningResultsExportNotice = React.useCallback((detail = {}) => {
    dispatchAppStatusNotice(buildRunningResultsExportNotice(detail));
  }, []);

  const prepareResultsSaveTarget = React.useCallback(async (promptForSaveLocation = false) => {
    if (!promptForSaveLocation) {
      return null;
    }

    return prepareBrowserSaveTarget({
      fallbackFileName: RESULTS_EXPORT_DEFAULT_FILE_NAME,
      mediaType: XLSX_MEDIA_TYPE,
      promptForSaveLocation: true,
    });
  }, []);

  const clearPendingDocumentWindow = React.useCallback(({ close = false } = {}) => {
    if (close && documentWindowRef.current) {
      closeExportDocumentWindow(documentWindowRef.current);
    }

    autoOpenFingerprintRef.current = "";
    documentWindowRef.current = null;
  }, []);

  const prepareResultsDocumentWindow = React.useCallback((fingerprint, openDocumentWindow = false, showDocumentWindow = true) => {
    clearPendingDocumentWindow({ close: true });
    if (!openDocumentWindow) {
      return null;
    }

    autoOpenFingerprintRef.current = normalizeFingerprint(fingerprint);
    if (!showDocumentWindow) {
      return null;
    }

    documentWindowRef.current = openExportDocumentWindow({
      detail: "Le téléchargement sera lancé automatiquement dès que le fichier aura fini d’être généré.",
      eyebrow: "Export Résultats",
      message: "Préparation du fichier XLSX en cours.",
      preferredHeight: 540,
      preferredWidth: 760,
      title: "Préparation de l’export résultats",
      windowName: `grain-results-export-${Date.now()}`,
    });
    return documentWindowRef.current;
  }, [clearPendingDocumentWindow]);

  const openLoadingToast = React.useCallback((message) => {
    if (loadingToastIdRef.current) {
      updateToast(loadingToastIdRef.current, {
        ...RESULTS_EXPORT_LOADING_TOAST,
        message,
        type: "loading",
      });
      return;
    }

    loadingToastIdRef.current = showToast({
      ...RESULTS_EXPORT_LOADING_TOAST,
      message,
    });
  }, [showToast, updateToast]);

  const closeLoadingToastWithResult = React.useCallback((toast) => {
    if (loadingToastIdRef.current) {
      updateToast(loadingToastIdRef.current, toast);
      loadingToastIdRef.current = null;
      return;
    }

    showToast(toast);
  }, [showToast, updateToast]);

  const dismissLoadingToast = React.useCallback(() => {
    if (!loadingToastIdRef.current) {
      return;
    }

    dismissToast(loadingToastIdRef.current);
    loadingToastIdRef.current = null;
  }, [dismissToast]);

  const applyFailureState = React.useCallback((nextState, notice) => {
    closeLoadingToastWithResult({
      duration: 8000,
      title: notice.title,
      message: notice.message,
      type: "error",
    });
    dispatchAppStatusNotice(notice);
    setExportState(nextState);
  }, [closeLoadingToastWithResult]);

  const handleExportApiError = React.useCallback((error, {
    fallbackMessage = "L'export n'a pas pu aboutir.",
    fingerprint = "",
  } = {}) => {
    if (isSaveLocationCancelledError(error) || (isExportApiError(error) && error.code === "save_cancelled")) {
      const currentState = exportStateRef.current;
      dismissLoadingToast();
      clearPendingDocumentWindow({ close: true });
      clearPendingSaveTarget();
      showToast({
        duration: 4500,
        title: "Enregistrement annulé",
        message: currentState.status === "ready_async"
          ? "Le fichier reste disponible. Relancez l'export quand vous voulez."
          : "Aucun fichier n'a été enregistré.",
        type: "info",
      });
      return;
    }

    const inlineMessage = !isExportApiError(error) && error && typeof error === "object" && typeof error.message === "string"
      ? error.message.trim()
      : "";
    const message = isExportApiError(error) && typeof error.message === "string" && error.message.trim()
      ? error.message.trim()
      : inlineMessage
        ? inlineMessage
      : fallbackMessage;
    const inlineCode = !isExportApiError(error) && error && typeof error === "object" && typeof error.code === "string"
      ? error.code.trim()
      : "";
    const code = isExportApiError(error) && typeof error.code === "string"
      ? error.code.trim()
      : inlineCode
        ? inlineCode
      : "export_generation_failed";
    const applyFailure = (nextState, notice) => {
      markExportDocumentWindowFailed(documentWindowRef.current, {
        eyebrow: "Export Résultats",
        message: notice.message,
        title: notice.title,
      });
      clearPendingDocumentWindow();
      clearPendingSaveTarget();
      applyFailureState(nextState, notice);
    };

    if (code === "latest_optimization_stale") {
      applyFailure(
        {
          ...buildDefaultResultsExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "blocked_stale",
        },
        buildFailedResultsExportNotice({
          message,
          title: "Export Résultats bloqué",
        }),
      );
      return;
    }

    if (code === "permission_denied") {
      applyFailure(
        {
          ...buildDefaultResultsExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "permission_denied",
        },
        buildFailedResultsExportNotice({
          message,
          title: "Export Résultats refusé",
        }),
      );
      return;
    }

    if (code === "latest_optimization_not_found") {
      applyFailure(
        {
          ...buildDefaultResultsExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "no_content",
        },
        buildFailedResultsExportNotice({
          message,
          title: "Aucun résultat exportable",
        }),
      );
      return;
    }

    if (code === "export_expired") {
      applyFailure(
        {
          ...buildDefaultResultsExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "expired",
        },
        buildFailedResultsExportNotice({
          message,
          title: "Export expiré",
        }),
      );
      return;
    }

    applyFailure(
      {
        ...buildDefaultResultsExportState(),
        errorCode: code,
        fingerprint,
        message,
        status: "failed",
      },
      buildFailedResultsExportNotice({
        message,
        title: "Export Résultats interrompu",
      }),
    );
  }, [applyFailureState, clearPendingDocumentWindow, clearPendingSaveTarget, dismissLoadingToast, showToast]);

  const performResultsExportDownload = React.useCallback(async ({
    documentWindow = null,
    exportId = "",
    fingerprint = "",
    openDocumentWindow = false,
    promptForSaveLocation = false,
    rowCount = null,
    saveTarget = null,
  } = {}) => {
    const currentState = exportStateRef.current;
    const resolvedExportId = typeof exportId === "string" && exportId.trim()
      ? exportId.trim()
      : currentState.exportId;
    if (!resolvedExportId || (!exportId && currentState.status !== "ready_async")) {
      return currentState;
    }

    const resolvedFingerprint = normalizeFingerprint(fingerprint) || currentState.fingerprint;
    const resolvedRowCount = Number.isFinite(Number(rowCount)) ? Number(rowCount) : currentState.rowCount;
    const resolvedSaveTarget = saveTarget
      || pendingSaveTargetRef.current
      || await prepareResultsSaveTarget(promptForSaveLocation);
    const resolvedDocumentWindow = documentWindow || (openDocumentWindow
      ? prepareResultsDocumentWindow(resolvedFingerprint, true, !resolvedSaveTarget?.handle)
      : null);

    try {
      const download = await downloadResultsExport(resolvedExportId, {
        documentWindow: resolvedDocumentWindow,
        saveTarget: resolvedSaveTarget,
      });
      clearPendingDocumentWindow();
      clearPendingSaveTarget();
      dispatchAppStatusNotice(buildDownloadedResultsExportNotice({
        destinationLabel: download.destinationLabel || RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
        fileName: download.fileName || RESULTS_EXPORT_DEFAULT_FILE_NAME,
        finishedAt: new Date().toISOString(),
        rowCount: resolvedRowCount,
        saveMode: download.saveMode || SAVE_MODE_BROWSER_DOWNLOAD,
      }));
      showToast({
        duration: 5000,
        title: download.saveMode === SAVE_MODE_FILE_SYSTEM ? "Fichier enregistré" : "Téléchargement lancé",
        message: download.saveMode === SAVE_MODE_FILE_SYSTEM
          ? "Le fichier XLSX a été enregistré à l’emplacement choisi."
          : "Le fichier XLSX est en cours de téléchargement.",
        type: "success",
      });
      return exportStateRef.current;
    } catch (error) {
      clearPendingSaveTarget();
      handleExportApiError(error, {
        fingerprint: resolvedFingerprint,
      });
      return exportStateRef.current;
    }
  }, [clearPendingDocumentWindow, clearPendingSaveTarget, handleExportApiError, prepareResultsDocumentWindow, prepareResultsSaveTarget, showToast]);

  const pollResultsExportStatus = React.useCallback(async (exportId) => {
    pollAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    pollAbortControllerRef.current = abortController;

    try {
      const payload = await getResultsExportStatus(exportId, {
        signal: abortController.signal,
      });
      if (payload?.status === "pending") {
        const nextState = buildPendingResultsExportState({
          exportId,
          fingerprint: normalizeFingerprint(payload?.fingerprint) || exportStateRef.current.fingerprint,
          payload,
          pollIntervalFallback: exportStateRef.current.pollIntervalMs || 2000,
          resultUrl: exportStateRef.current.resultUrl,
          rowCountFallback: exportStateRef.current.rowCount,
          startedAtFallback: exportStateRef.current.startedAt,
        });
        setExportState(nextState);
        return;
      }

      if (payload?.status === "completed") {
        const nextState = buildReadyResultsExportState({
          exportId,
          fingerprint: normalizeFingerprint(payload?.fingerprint) || exportStateRef.current.fingerprint,
          payload,
          resultUrl: exportStateRef.current.resultUrl,
          rowCountFallback: exportStateRef.current.rowCount,
          startedAtFallback: exportStateRef.current.startedAt,
        });

        setExportState(nextState);
        if (autoOpenFingerprintRef.current && autoOpenFingerprintRef.current === nextState.fingerprint) {
          const pendingDocumentWindow = documentWindowRef.current;
          const pendingSaveTarget = pendingSaveTargetRef.current;
          dismissLoadingToast();
          clearPendingDocumentWindow();
          await performResultsExportDownload({
            documentWindow: pendingDocumentWindow,
            exportId,
            fingerprint: nextState.fingerprint,
            rowCount: nextState.rowCount,
            saveTarget: pendingSaveTarget,
          });
          return;
        }

        closeLoadingToastWithResult({
          duration: 7000,
          title: "Export prêt",
          message: nextState.message,
          type: "success",
        });
        dispatchAppStatusNotice(buildCompletedResultsExportNotice(nextState));
        return;
      }

      if (payload?.status === "failed") {
        handleExportApiError(new Error(typeof payload?.error === "string" ? payload.error : typeof payload?.message === "string" ? payload.message : "L'export a échoué."), {
          fallbackMessage: typeof payload?.message === "string" ? payload.message : "L'export a échoué.",
          fingerprint: exportStateRef.current.fingerprint,
        });
        return;
      }

      if (payload?.status === "expired") {
        handleExportApiError({ code: "export_expired", message: typeof payload?.message === "string" ? payload.message : "L'export n'est plus disponible." }, {
          fingerprint: exportStateRef.current.fingerprint,
        });
        return;
      }

      handleExportApiError(new Error("Le statut d'export reçu est invalide."), {
        fingerprint: exportStateRef.current.fingerprint,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      handleExportApiError(error, {
        fallbackMessage: "Le suivi de l'export n'est plus disponible.",
        fingerprint: exportStateRef.current.fingerprint,
      });
    }
  }, [clearPendingDocumentWindow, closeLoadingToastWithResult, dismissLoadingToast, handleExportApiError, performResultsExportDownload]);

  const restoreResultsExportFromHistory = React.useCallback(async (fingerprint, {
    resultUrl = RESULTS_EXPORT_READY_ROUTE,
  } = {}) => {
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveResultsReadyRoute(resultUrl);
    if (!normalizedFingerprint) {
      return;
    }

    const currentState = exportStateRef.current;
    if (currentState.status === "launching" || currentState.status === "pending_async") {
      return;
    }

    if (currentState.fingerprint === normalizedFingerprint && currentState.exportId) {
      return;
    }

    if (hydratedFingerprintRef.current === normalizedFingerprint) {
      return;
    }

    hydratedFingerprintRef.current = normalizedFingerprint;
    historyAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    historyAbortControllerRef.current = abortController;

    try {
      const payload = await getResultsExportHistory({
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) {
        return;
      }

      const matchingItem = Array.isArray(payload?.items)
        ? payload.items.find((item) => normalizeFingerprint(item?.fingerprint) === normalizedFingerprint)
        : null;
      const exportId = typeof matchingItem?.export_id === "string" ? matchingItem.export_id : "";
      if (!matchingItem || !exportId) {
        return;
      }

      if (matchingItem.status === "completed") {
        const nextState = buildReadyResultsExportState({
          exportId,
          fingerprint: normalizedFingerprint,
          payload: matchingItem,
          resultUrl: resolvedResultUrl,
        });
        setExportState(nextState);
        dispatchAppStatusNotice(buildCompletedResultsExportNotice(nextState));
        return;
      }

      if (matchingItem.status === "pending") {
        const nextState = buildPendingResultsExportState({
          exportId,
          fingerprint: normalizedFingerprint,
          payload: matchingItem,
          resultUrl: resolvedResultUrl,
        });
        setExportState(nextState);
        publishRunningResultsExportNotice({
          destinationLabel: pendingSaveTargetRef.current?.destinationLabel || RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
          message: nextState.message,
          rowCount: nextState.rowCount,
          startedAt: nextState.startedAt,
        });
      }
    } catch {
      if (abortController.signal.aborted) {
        return;
      }
    }
  }, [publishRunningResultsExportNotice]);

  React.useEffect(() => {
    if (exportState.status !== "pending_async" || !exportState.exportId || typeof window === "undefined") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void pollResultsExportStatus(exportState.exportId);
    }, exportState.pollIntervalMs || 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [exportState.exportId, exportState.pollIntervalMs, exportState.status, pollResultsExportStatus]);

  const launchTrackedResultsExport = React.useCallback(async ({
    blocked = false,
    blockedReason = "",
    fingerprint = "",
    openDocumentWindow = false,
    promptForSaveLocation = false,
    resultUrl = RESULTS_EXPORT_READY_ROUTE,
  } = {}) => {
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveResultsReadyRoute(resultUrl);
    if (exportStateRef.current.status === "launching" || exportStateRef.current.status === "pending_async") {
      return exportStateRef.current;
    }

    if (blocked) {
      handleExportApiError({
        code: "latest_optimization_stale",
        message: blockedReason || "Le snapshot d'optimisation est obsolète. Relancez l'optimisation avant d'exporter les résultats.",
      }, {
        fingerprint: normalizedFingerprint,
      });
      return exportStateRef.current;
    }

    let saveTarget = null;
    try {
      saveTarget = await prepareResultsSaveTarget(promptForSaveLocation);
    } catch (error) {
      handleExportApiError(error, {
        fingerprint: normalizedFingerprint,
      });
      return exportStateRef.current;
    }

    clearResultsExportNotice();
    clearPendingSaveTarget();
    historyAbortControllerRef.current?.abort();
    hydratedFingerprintRef.current = normalizedFingerprint;
    const documentWindow = prepareResultsDocumentWindow(
      normalizedFingerprint,
      openDocumentWindow,
      !saveTarget?.handle,
    );
    openLoadingToast("Préparation du fichier XLSX en arrière-plan. Vous pouvez continuer à naviguer.");
    const startedAt = new Date().toISOString();
    setExportState({
      ...buildDefaultResultsExportState(),
      fingerprint: normalizedFingerprint,
      message: "Préparation du fichier XLSX...",
      startedAt,
      resultUrl: resolvedResultUrl,
      status: "launching",
    });
    publishRunningResultsExportNotice({
      destinationLabel: saveTarget?.destinationLabel || RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
      startedAt,
    });

    try {
      const result = await launchResultsExport({ documentWindow, saveTarget });
      if (result.mode === "sync") {
        clearPendingDocumentWindow();
        clearPendingSaveTarget();
        dismissLoadingToast();
        dispatchAppStatusNotice(buildDownloadedResultsExportNotice({
          destinationLabel: result.download?.destinationLabel || RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
          fileName: result.download?.fileName || RESULTS_EXPORT_DEFAULT_FILE_NAME,
          finishedAt: new Date().toISOString(),
          saveMode: result.download?.saveMode || SAVE_MODE_BROWSER_DOWNLOAD,
        }));
        showToast({
          duration: 6000,
          title: result.download?.saveMode === SAVE_MODE_FILE_SYSTEM ? "Fichier enregistré" : "Export téléchargé",
          message: result.download?.saveMode === SAVE_MODE_FILE_SYSTEM
            ? "Le fichier XLSX a été enregistré immédiatement à l’emplacement choisi."
            : "Le fichier XLSX a été téléchargé immédiatement.",
          type: "success",
        });
        setExportState(buildDefaultResultsExportState());
        return buildDefaultResultsExportState();
      }

      const payload = result.payload || {};
      pendingSaveTargetRef.current = saveTarget || null;
      const nextState = buildPendingResultsExportState({
        exportId: typeof payload?.export_id === "string" ? payload.export_id : "",
        fingerprint: normalizedFingerprint,
        payload,
        resultUrl: resolvedResultUrl,
        startedAtFallback: startedAt,
      });
      setExportState(nextState);
      publishRunningResultsExportNotice({
        destinationLabel: pendingSaveTargetRef.current?.destinationLabel || RESULTS_EXPORT_BROWSER_DESTINATION_LABEL,
        message: nextState.message || "Préparation du fichier XLSX en arrière-plan.",
        rowCount: nextState.rowCount,
        startedAt: nextState.startedAt,
      });
      return nextState;
    } catch (error) {
      handleExportApiError(error, {
        fingerprint: normalizedFingerprint,
      });
      return exportStateRef.current;
    }
  }, [clearPendingDocumentWindow, clearPendingSaveTarget, clearResultsExportNotice, dismissLoadingToast, handleExportApiError, openLoadingToast, prepareResultsDocumentWindow, prepareResultsSaveTarget, publishRunningResultsExportNotice, showToast]);

  const downloadReadyResultsExport = React.useCallback(async ({
    documentWindow = null,
    exportId = "",
    fingerprint = "",
    openDocumentWindow = false,
    promptForSaveLocation = false,
    rowCount = null,
    saveTarget = null,
  } = {}) => {
    const currentState = exportStateRef.current;
    if ((!exportId && currentState.status !== "ready_async") || !(exportId || currentState.exportId)) {
      return currentState;
    }

    return performResultsExportDownload({
      documentWindow,
      exportId: exportId || currentState.exportId,
      fingerprint: fingerprint || currentState.fingerprint,
      openDocumentWindow,
      promptForSaveLocation,
      rowCount: rowCount ?? currentState.rowCount,
      saveTarget,
    });
  }, [performResultsExportDownload]);

  const syncResultsExportFingerprint = React.useCallback((fingerprint, {
    resultUrl = RESULTS_EXPORT_READY_ROUTE,
  } = {}) => {
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveResultsReadyRoute(resultUrl);
    const currentState = exportStateRef.current;
    if (!normalizedFingerprint) {
      if (currentState.status === "launching" || currentState.status === "pending_async") {
        return;
      }

      clearResultsExportNotice();
      clearPendingSaveTarget();
      dismissLoadingToast();
      pollAbortControllerRef.current?.abort();
      historyAbortControllerRef.current?.abort();
      hydratedFingerprintRef.current = "";
      clearPendingDocumentWindow();
      setExportState(buildDefaultResultsExportState());
      return;
    }

    if (currentState.status === "launching" || currentState.status === "pending_async") {
      return;
    }

    if (currentState.fingerprint && currentState.fingerprint !== normalizedFingerprint) {
      clearResultsExportNotice();
      clearPendingSaveTarget();
      dismissLoadingToast();
      hydratedFingerprintRef.current = "";
      setExportState(buildDefaultResultsExportState());
    }

    if (currentState.fingerprint === normalizedFingerprint && currentState.exportId) {
      if (currentState.resultUrl !== resolvedResultUrl) {
        setExportState({
          ...currentState,
          resultUrl: resolvedResultUrl,
        });
      }
      return;
    }

    void restoreResultsExportFromHistory(normalizedFingerprint, { resultUrl: resolvedResultUrl });
  }, [clearPendingDocumentWindow, clearPendingSaveTarget, clearResultsExportNotice, dismissLoadingToast, restoreResultsExportFromHistory]);

  const contextValue = React.useMemo(() => ({
    downloadReadyResultsExport,
    exportState,
    launchResultsExport: launchTrackedResultsExport,
    syncResultsExportFingerprint,
  }), [downloadReadyResultsExport, exportState, launchTrackedResultsExport, syncResultsExportFingerprint]);

  return (
    <ResultsExportTrackerContext.Provider value={contextValue}>
      {children}
    </ResultsExportTrackerContext.Provider>
  );
}

export function useResultsExportTracker() {
  const context = React.useContext(ResultsExportTrackerContext);
  if (context) {
    return context;
  }

  return {
    downloadReadyResultsExport: async () => buildDefaultResultsExportState(),
    exportState: buildDefaultResultsExportState(),
    launchResultsExport: async () => buildDefaultResultsExportState(),
    syncResultsExportFingerprint() {},
  };
}